/**
 * Words for fleet scan policy (release 3.3, R3-OI-194): where a device's scan
 * hours come from. Shared by the Device groups panel and the device drawer.
 */
import type { DeviceGroupRule, EffectiveScanPolicy, ScanPolicySource } from '@/lib/api';

export const SOURCE_ORDER: ScanPolicySource[] = ['device', 'group', 'business_unit', 'tenant', 'install', 'default'];

export const SOURCE_TITLE: Record<ScanPolicySource, string> = {
  device: 'Device setting',
  group: 'Device group',
  business_unit: 'Business unit',
  tenant: 'Whole tenant',
  install: 'Installer',
  default: 'Any time',
};

/** "from the group Finance laptops", "from the tenant policy", ... */
export function sourceText(e: EffectiveScanPolicy, unitName?: string | null): string {
  switch (e.source) {
    case 'device':
      return "this device's own setting";
    case 'group':
      return `the device group ${e.groupName ?? ''}`.trim();
    case 'business_unit':
      return unitName ? `the business unit policy (${unitName})` : 'the business unit policy';
    case 'tenant':
      return 'the tenant policy';
    case 'install':
      return 'the hours baked in at install';
    default:
      return 'nothing set (any time of day)';
  }
}

/** A rule in plain words: "OS windows, hostname contains fin-". */
export function ruleText(rule: DeviceGroupRule | null): string {
  if (!rule) return '';
  const parts: string[] = [];
  if (rule.os) parts.push(`OS ${rule.os}`);
  if (rule.agentType) parts.push(`type ${rule.agentType.replace(/_/g, ' ')}`);
  if (rule.hostnameContains) parts.push(`hostname contains "${rule.hostnameContains}"`);
  if (rule.ipPrefix) parts.push(`address starts ${rule.ipPrefix}`);
  if (rule.version) parts.push(`version ${rule.version}`);
  return parts.join(', ');
}

/** A short form of a window for tables: "12:30-14:00" or "any time". */
export function shortWindow(w: string | null | undefined): string {
  if (!w) return 'not set';
  const [a, b] = w.split('-');
  return a === b ? 'any time' : w;
}

const INDIAN = new Intl.NumberFormat('en-IN');
export const count = (n: number): string => INDIAN.format(n);
